import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EffectRef,
  effect,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';

import {DialogModule} from '@syncfusion/ej2-angular-popups';

import {HeadlineComponent} from '../../../../shared/elements/headline/headline.component';
import {FormInputComponent} from '../../../../shared/elements/form-input/form-input.component';
import {TranslatePipe} from '../../../../shared/pipes/translate.pipe';
import {DialogService} from '../../../../shared/services/dialog.service';
import {RouterService} from '../../../../shared/services/router.service';
import {DashboardLinkResource} from '../../../../shared/types/generated/dashboardLinkResource';
import {HomeLinksComponent} from '../../../home/custom-links/custom-links.component';
import {DashboardLinksStateService} from '../../../../shared/services/dashboard-links-state.service';

@Component({
  selector: 'app-dialog-home-links-all',
  imports: [
    DialogModule,
    HeadlineComponent,
    TranslatePipe,
    HomeLinksComponent,
    ReactiveFormsModule,
    FormInputComponent,
  ],
  templateUrl: './dialog-home-links-all.component.html',
  styleUrl: './dialog-home-links-all.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogHomeLinksAllComponent {
  public readonly dialogService: DialogService = inject(DialogService);

  private readonly routerService: RouterService = inject(RouterService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly dashboardLinksStateService: DashboardLinksStateService = inject(DashboardLinksStateService);

  public readonly visible: InputSignal<boolean> = input(false);

  public readonly visibleChange: OutputEmitterRef<boolean> = output<boolean>();

  public readonly homeLinksForm: FormGroup = this.formBuilder.group({
    searchString: [''],
  });

  private readonly searchString: Signal<string | null> = toSignal(
    this.homeLinksForm.controls['searchString'].valueChanges,
    {initialValue: this.homeLinksForm.controls['searchString'].value}
  );

  private readonly wasVisible: WritableSignal<boolean> = signal(false);

  public readonly loading: Signal<boolean> = computed(() =>
    this.dashboardLinksStateService.loadState() === 'loading'
  );

  public readonly query: Signal<string> = computed(() =>
    String(this.searchString() ?? '')
  );

  public readonly linksPage: WritableSignal<number> = linkedSignal(() => {
    this.query();
    return 1;
  });

  public readonly allLinks: WritableSignal<DashboardLinkResource[]> = linkedSignal(() =>
    this.dashboardLinksStateService.links()
  );

  public readonly displayLinks: Signal<DashboardLinkResource[]> = computed(() => {
    const searchTerm = this.query().trim().toLowerCase();
    const links = this.allLinks();

    if (!searchTerm) {
      return links;
    }

    return links.filter((link) => {
      const name = (link.name ?? '').toLowerCase();
      const url = (link.url ?? '').toLowerCase();

      return name.includes(searchTerm) || url.includes(searchTerm);
    });
  });

  private readonly visibleEffect: EffectRef = effect(() => {
    const isVisible = this.visible();
    const wasVisible = untracked(() => this.wasVisible());

    if (isVisible && !wasVisible) {
      this.loadLinks();
    }

    this.wasVisible.set(isVisible);
  });

  public close(): void {
    this.resetList();
    this.visibleChange.emit(false);
  }

  public resetList(): void {
    this.homeLinksForm.controls['searchString'].setValue('');
    this.linksPage.set(1);
  }

  public onLinkClick(link: DashboardLinkResource): void {
    if (this.routerService.isDashboardLinkInternal(link.url)) {
      this.close();
      this.routerService.navigateTo(link.url);
      return;
    }

    this.routerService.openURL(link.url);
  }

  private loadLinks(): void {
    this.dashboardLinksStateService.loadLinks(true);
    this.linksPage.set(1);
  }
}
